import {
  Button,
  Chip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@prelude-ai/design-system"
import { CheckIcon, ChevronDownIcon, InfoIcon, icons } from "lucide-react"
import { useMemo } from "react"
import type { AgentMetadata as Agent, AgentIconName, AgentStatus } from "../../types/index.ts"
import { useIsMobile } from "../hooks/useIsMobile"

const statusConfig: Record<AgentStatus, { label: string; className: string }> = {
  stable: {
    label: "Stable",
    className: "bg-green-300 dark:bg-green-800 dark:text-green-50 text-green-900 border-transparent"
  },
  beta: {
    label: "Beta",
    className: "bg-yellow-300 dark:bg-yellow-800 dark:text-yellow-50 text-yellow-900 border-transparent"
  },
  experimental: {
    label: "Experimental",
    className: "bg-orange-300 dark:bg-orange-800 dark:text-orange-50 text-orange-900 border-transparent"
  },
  mock: { label: "Mock", className: "bg-red-300 dark:bg-red-800 dark:text-red-50 text-red-900 border-transparent" }
}

function AgentIcon({ iconName, className }: { iconName?: AgentIconName; className?: string }) {
  if (!iconName) return null
  const Icon = icons[iconName]
  if (!Icon) return null
  return <Icon className={className} />
}

type AgentSelectorProps = {
  agents: Agent[]
  selectedAgent: string | null
  onSelectAgent: (agentName: string) => void
  backgroundMode: boolean
  onBackgroundModeChange: (enabled: boolean) => void
}

export default function AgentSelector({
  agents,
  selectedAgent,
  onSelectAgent,
  backgroundMode,
  onBackgroundModeChange
}: AgentSelectorProps) {
  const currentAgent = agents.find((a) => a.name === selectedAgent)
  const isMobile = useIsMobile()

  // Group agents by category and sort
  const groupedAgents = useMemo(() => {
    const groups: Record<string, Agent[]> = {}

    for (const agent of agents) {
      const category = agent.category || "Other"
      if (!groups[category]) groups[category] = []
      groups[category].push(agent)
    }

    // Sort agents within each group alphabetically
    for (const category of Object.keys(groups)) {
      groups[category]?.sort((a, b) => a.name.localeCompare(b.name))
    }

    // Sort categories alphabetically (Demo and Other last)
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      if (a === "Demo") return 1
      if (b === "Demo") return -1
      if (a === "Other") return 1
      if (b === "Other") return -1
      return a.localeCompare(b)
    })

    return sortedCategories.map((category) => ({
      category,
      agents: groups[category] ?? []
    }))
  }, [agents])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="justify-between min-w-0">
            <span className="flex items-center gap-2 truncate">
              {currentAgent?.icon && <AgentIcon iconName={currentAgent.icon} className="h-4 w-4 shrink-0" />}
              {currentAgent?.name || "Select an agent"}
            </span>
            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          aria-label="Agent Select"
          className={isMobile ? "w-72 max-h-[min(500px,calc(100vh-100px))] overflow-y-auto" : "w-48"}
        >
          {isMobile
            ? // Mobile: Flat list with category headers
              groupedAgents.map(({ category, agents: categoryAgents }, index) => (
                <DropdownMenuGroup key={category}>
                  {index > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {category}
                  </DropdownMenuLabel>
                  {categoryAgents.map((agent) => (
                    <DropdownMenuItem
                      key={agent.name}
                      onClick={() => onSelectAgent(agent.name)}
                      className="cursor-pointer py-2 px-2"
                    >
                      <div className="flex items-start gap-2 w-full">
                        <div className="w-4 pt-0.5 shrink-0">
                          {selectedAgent === agent.name && <CheckIcon className="h-4 w-4" />}
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="flex items-center gap-2">
                            {agent.icon && (
                              <AgentIcon iconName={agent.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <span className="font-medium text-sm">{agent.name}</span>
                            <Chip className={`text-[10px] px-1.5 py-0 ${statusConfig[agent.status].className}`}>
                              {statusConfig[agent.status].label}
                            </Chip>
                          </div>
                          <span className="text-xs text-muted-foreground leading-snug line-clamp-2">
                            {agent.description}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              ))
            : // Desktop: Nested submenus
              groupedAgents.map(({ category, agents: categoryAgents }) => (
                <DropdownMenuSub key={category}>
                  <DropdownMenuSubTrigger className="py-2">{category}</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="w-80 max-h-100 overflow-y-auto">
                      {categoryAgents.map((agent) => (
                        <DropdownMenuItem
                          key={agent.name}
                          onClick={() => onSelectAgent(agent.name)}
                          className="cursor-pointer py-2.5 px-3"
                        >
                          <div className="flex items-start gap-2 w-full">
                            <div className="w-4 pt-0.5 shrink-0">
                              {selectedAgent === agent.name && <CheckIcon className="h-4 w-4" />}
                            </div>
                            <div className="flex flex-col gap-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {agent.icon && (
                                  <AgentIcon iconName={agent.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                                )}
                                <span className="font-medium text-sm">{agent.name}</span>
                                <Chip className={`text-[10px] px-1.5 py-0 ${statusConfig[agent.status].className}`}>
                                  {statusConfig[agent.status].label}
                                </Chip>
                              </div>
                              <span className="text-xs text-muted-foreground leading-relaxed">{agent.description}</span>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p>Background Mode will run the agent as if running in the background. It will not ask for user input.</p>
          </TooltipContent>
        </Tooltip>
        <Label className="shrink-0">Background Mode</Label>
        <Switch checked={backgroundMode} onCheckedChange={onBackgroundModeChange} />
        {currentAgent && (
          <Chip className={`text-xs shrink-0 ${statusConfig[currentAgent.status].className}`}>
            {statusConfig[currentAgent.status].label}
          </Chip>
        )}
      </div>

      {currentAgent?.model && <Chip className="text-xs">{currentAgent.model}</Chip>}
    </>
  )
}
